import { SuperOpsStateManager } from "./SuperOpsStateManager";
import { cache, ZomentumStorageKeys } from "@/Contracts";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import { prependBasePath } from "@/Utils/routing/getAppPathname";
import { SuperOpsState } from "./interfaces";
import { SuperOpsSdkService } from "./SuperOpsSdkService";

/**
 * Bridges the SuperOps host SDK state lifecycle to Zomentum's Redux/local state.
 * The host drives this via SDK events: `restoreData` (pushed after the widget
 * mounts) and `persistData` (pulled before it unmounts). The sessionStorage
 * pending-restore path is retained for our own internal login redirect, which
 * the host's persist/restore cannot survive.
 */
class SuperOpsMessageService {
	private static instance: SuperOpsMessageService;
	private stateManager: SuperOpsStateManager;
	private isInitialized = false;

	private constructor() {
		this.stateManager = new SuperOpsStateManager();
	}

	static getInstance(): SuperOpsMessageService {
		if (!SuperOpsMessageService.instance) {
			SuperOpsMessageService.instance = new SuperOpsMessageService();
		}
		return SuperOpsMessageService.instance;
	}

	initialize(): void {
		if (this.isInitialized) return;

		// Only initialize if we're in embedded context
		if (!isEmbedded()) return;

		this.registerSdkHandlers();
		this.checkForPendingRestoration();
		this.isInitialized = true;
	}

	private registerSdkHandlers(): void {
		SuperOpsSdkService.onRestoreData(saved => {
			if (saved) {
				void this.handleRestoreState(saved);
			}
		});
		SuperOpsSdkService.onPersistData(() => this.gatherCurrentData());
	}

	private async handleRestoreState(
		restoreState: SuperOpsState
	): Promise<void> {
		try {
			if (!this.isAuthenticated()) {
				// Save state for after login and redirect to login
				this.stateManager.saveRestoreStateForLater(restoreState);
				await this.navigateToLogin();
				return;
			}

			await this.stateManager.restoreState(restoreState);
		} catch (error) {
			console.error("Failed to handle restore state:", error);
		}
	}

	private gatherCurrentData(): SuperOpsState {
		try {
			if (!this.isAuthenticated()) {
				console.warn(
					"SuperOps persist data skipped: User not authenticated"
				);
				return { route: "" };
			}
			return this.stateManager.getCurrentState();
		} catch (error) {
			console.error("Failed to gather current data:", error);
			return { route: "" };
		}
	}

	private isAuthenticated(): boolean {
		try {
			return !!cache.get(ZomentumStorageKeys.Token);
		} catch (error) {
			return false;
		}
	}

	private async navigateToLogin(): Promise<void> {
		const loginUrl = prependBasePath("/login") + "?embed=true";
		window.history.pushState(null, "", loginUrl);
		const event = new PopStateEvent("popstate");
		window.dispatchEvent(event);
	}

	private checkForPendingRestoration(): void {
		// Check if user just logged in and we have pending restoration
		this.stateManager.checkForPendingRestoration();
	}
}

export { SuperOpsMessageService };
